/**
 * Import Graph Flow Converter
 * Issue #500: Converts ImportGraphData to UnifiedDiagramData for use with ReactFlowRenderer.
 */

import type { UnifiedDiagramData, UnifiedDiagramNode, UnifiedDiagramEdge } from '@veodiagram/shared';

// Types for import graph data from API
export interface ImportGraphNode {
  id: string;
  name: string;
  filePath: string;
  role: 'controller' | 'service' | 'repository' | 'middleware' | 'utility' | 'model' | 'unknown';
  layer: number;
  exportCount: number;
  importCount: number;
  importedBy: string[];
  imports: string[];
  isBarrel: boolean;
  hasCircularDep: boolean;
  isEntryPoint: boolean;
}

export interface ImportGraphEdge {
  id: string;
  source: string;
  target: string;
  type: 'import' | 'reexport' | 'typeOnly';
  imports: string[];
  isCircular: boolean;
}

export interface ImportGraphData {
  projectId: string;
  projectName: string;
  graph: {
    nodes: ImportGraphNode[];
    edges: ImportGraphEdge[];
    circularDependencies: string[][];
    layers: {
      controllers: string[];
      services: string[];
      repositories: string[];
      middleware: string[];
      utilities: string[];
      models: string[];
      unknown: string[];
    };
    barrels: string[];
  };
  metadata: {
    totalFiles: number;
    totalImports: number;
    totalExports: number;
    circularDependencyCount: number;
    entryPointCount: number;
    barrelFileCount: number;
  };
  generatedAt: string;
  cached?: boolean;
  cachedAt?: string;
}

// Role colors for import graph nodes
export const ROLE_COLORS: Record<string, string> = {
  controller: '#3b82f6',
  service: '#8b5cf6',
  repository: '#f59e0b',
  middleware: '#10b981',
  utility: '#6b7280',
  model: '#ec4899',
  unknown: '#9ca3af',
};

// ============= Grouped Graph (Issue #565 Phase 1) =============

export type GroupByMode = 'directory' | 'role' | 'layer';

function getGroupKey(node: ImportGraphNode, mode: GroupByMode): string {
  switch (mode) {
    case 'directory': {
      const parts = node.filePath.replace(/^\//, '').split('/');
      return parts.length > 1 ? parts.slice(0, -1).join('/') : '(root)';
    }
    case 'role':
      return node.role;
    case 'layer':
      return `Layer ${node.layer}`;
  }
}

/**
 * Convert ImportGraphData to grouped UnifiedDiagramData where directories/roles/layers
 * become collapsible group container nodes (parentId-based React Flow groups).
 */
export function convertWithGroups(
  data: ImportGraphData,
  groupBy: GroupByMode,
  collapsedGroups: Set<string> = new Set()
): UnifiedDiagramData {
  // Partition nodes by group key
  const groupMap = new Map<string, ImportGraphNode[]>();
  for (const node of data.graph.nodes) {
    const key = getGroupKey(node, groupBy);
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(node);
  }

  const groupNodes: UnifiedDiagramNode[] = [];
  const childNodes: UnifiedDiagramNode[] = [];

  for (const [key, members] of groupMap.entries()) {
    const groupId = `group::${key}`;
    const isCollapsed = collapsedGroups.has(groupId);

    groupNodes.push({
      id: groupId,
      type: 'importGroupNode' as UnifiedDiagramNode['type'],
      label: key,
      position: { x: 0, y: 0 },
      data: {
        groupKey: key,
        memberCount: members.length,
        isCollapsed,
        groupId,
      },
    });

    if (!isCollapsed) {
      for (const node of members) {
        childNodes.push({
          id: node.id,
          type: 'importGraphNode' as UnifiedDiagramNode['type'],
          label: node.name,
          position: { x: 0, y: 0 },
          parentId: groupId,
          data: {
            filePath: node.filePath,
            role: node.role,
            layer: node.layer,
            exportCount: node.exportCount,
            importCount: node.importCount,
            importedBy: node.importedBy,
            imports: node.imports,
            isBarrel: node.isBarrel,
            hasCircularDep: node.hasCircularDep,
            isEntryPoint: node.isEntryPoint,
            name: node.name,
          },
          style: {
            borderColor: ROLE_COLORS[node.role] || ROLE_COLORS.unknown,
          },
        });
      }
    }
  }

  // Build aggregate edges between groups for collapsed groups,
  // keep per-file edges for expanded groups
  const nodeToGroupId = new Map<string, string>();
  for (const node of data.graph.nodes) {
    const key = getGroupKey(node, groupBy);
    nodeToGroupId.set(node.id, `group::${key}`);
  }
  const visibleNodeIds = new Set(childNodes.map(n => n.id));

  const edges: UnifiedDiagramEdge[] = [];
  const aggregateEdgeMap = new Map<string, number>(); // "groupA→groupB" → count

  for (const edge of data.graph.edges) {
    const srcGroup = nodeToGroupId.get(edge.source);
    const tgtGroup = nodeToGroupId.get(edge.target);
    const srcVisible = visibleNodeIds.has(edge.source);
    const tgtVisible = visibleNodeIds.has(edge.target);

    if (srcVisible && tgtVisible) {
      // Both nodes visible — use per-file edge
      edges.push({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.imports.length > 3 ? `${edge.imports.length} items` : edge.imports.join(', '),
        type: 'dependency' as UnifiedDiagramEdge['type'],
        animated: edge.type === 'reexport',
        style: {
          strokeColor: edge.isCircular ? '#ef4444' : edge.type === 'reexport' ? '#06b6d4' : '#94a3b8',
          strokeWidth: edge.isCircular ? 2 : 1,
          strokeDasharray: edge.type === 'typeOnly' ? '5 5' : undefined,
        },
        data: { imports: edge.imports, isCircular: edge.isCircular, importType: edge.type },
      });
    } else if (srcGroup && tgtGroup && srcGroup !== tgtGroup) {
      // Cross-group edge — aggregate
      const key = `${srcGroup}→${tgtGroup}`;
      aggregateEdgeMap.set(key, (aggregateEdgeMap.get(key) || 0) + 1);
    }
  }

  // Emit aggregate edges between groups
  for (const [key, count] of aggregateEdgeMap.entries()) {
    const [src, tgt] = key.split('→');
    edges.push({
      id: `agg::${key}`,
      source: src,
      target: tgt,
      label: `${count} imports`,
      type: 'dependency' as UnifiedDiagramEdge['type'],
      animated: false,
      style: { strokeColor: '#94a3b8', strokeWidth: Math.min(count, 4) },
      data: { aggregateCount: count },
    });
  }

  return {
    metadata: {
      title: `Import Graph: ${data.projectName}`,
      diagramType: 'importGraph',
      layoutDirection: 'LR',
      layoutAlgorithm: 'dagre',
    },
    nodes: [...groupNodes, ...childNodes],
    edges,
  };
}

/**
 * Convert ImportGraphData to UnifiedDiagramData for the shared ReactFlowRenderer.
 */
export function convertImportGraphToUnified(data: ImportGraphData): UnifiedDiagramData {
  const nodes: UnifiedDiagramNode[] = data.graph.nodes.map((node) => ({
    id: node.id,
    type: 'importGraphNode' as UnifiedDiagramNode['type'],
    label: node.name,
    position: { x: 0, y: 0 },
    data: {
      filePath: node.filePath,
      role: node.role,
      layer: node.layer,
      exportCount: node.exportCount,
      importCount: node.importCount,
      importedBy: node.importedBy,
      imports: node.imports,
      isBarrel: node.isBarrel,
      hasCircularDep: node.hasCircularDep,
      isEntryPoint: node.isEntryPoint,
      name: node.name,
    },
    style: {
      borderColor: ROLE_COLORS[node.role] || ROLE_COLORS.unknown,
    },
  }));

  const edges: UnifiedDiagramEdge[] = data.graph.edges.map((edge) => {
    const isCircular = edge.isCircular;
    const isReexport = edge.type === 'reexport';
    const isTypeOnly = edge.type === 'typeOnly';

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.imports.length > 3 ? `${edge.imports.length} items` : edge.imports.join(', '),
      type: 'dependency' as UnifiedDiagramEdge['type'],
      animated: isReexport,
      style: {
        strokeColor: isCircular ? '#ef4444' : isReexport ? '#06b6d4' : '#94a3b8',
        strokeWidth: isCircular ? 2 : 1,
        strokeDasharray: isTypeOnly ? '5 5' : undefined,
      },
      data: {
        imports: edge.imports,
        isCircular: edge.isCircular,
        importType: edge.type,
      },
    };
  });

  return {
    metadata: {
      title: `Import Graph: ${data.projectName}`,
      diagramType: 'importGraph',
      layoutDirection: 'LR',
      layoutAlgorithm: 'dagre',
    },
    nodes,
    edges,
  };
}
